import React, { useState, useEffect, useRef } from 'react';
import { useCart } from '../context/CartContext';
import { useProducts } from '../context/ProductsContext';
import { ArrowRight, Star } from 'lucide-react';
import SEO from './SEO';
import './HomePage.css';

const HomePage = ({ onProductClick }) => {
  const { addToCart } = useCart();
  const { products, getFeaturedProducts } = useProducts();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const intervalRef = useRef(null);

  // Sélectionner 6 produits aléatoires (ou produits vedettes si disponibles)
  const getRandomProducts = () => {
    const featured = getFeaturedProducts();
    if (featured.length >= 6) {
      return featured.slice(0, 6);
    }
    const shuffled = [...products].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 6);
  };

  // Initialiser les produits au chargement et démarrer la rotation
  useEffect(() => {
    if (products.length > 0) {
      setFeaturedProducts(getRandomProducts());
      
      // Nettoyer l'ancien intervalle s'il existe
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      console.log('🔄 Démarrage de la rotation automatique (15 secondes)');
      
      // Créer le nouvel intervalle
      intervalRef.current = setInterval(() => {
        setFeaturedProducts(getRandomProducts());
        console.log('🔄 Produits mis à jour -', new Date().toLocaleTimeString());
      }, 15000); // 15 secondes
    }
    
    // Nettoyage au démontage
    return () => {
      if (intervalRef.current) {
        console.log('🔄 Arrêt de la rotation automatique');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products.length]);

  const handleAddToCart = (product, e) => {
    e?.stopPropagation();
    console.log('🛒 HomePage - Ajout au panier:', product.name);
    if (!product) {
      console.error('❌ Produit invalide dans HomePage');
      return;
    }
    // Vérifier si le produit est en rupture de stock
    if (product.stock === 0) {
      console.warn('⚠️ Produit en rupture de stock:', product.name);
      return;
    }
    addToCart(product, 1);
  };

  const handleProductClick = (product) => {
    if (onProductClick) {
      onProductClick(product);
    }
  };

  return (
    <>
      <SEO 
        title="Accueil - Viandes Bio de Qualité au Lude"
        description="La Ferme À L'Ancienne - Exploitation familiale depuis 1936, agriculture biologique depuis 2009. Découvrez nos viandes de qualité supérieure : bœuf bio, porc bio, agneau bio, volaille bio au Lude (72800)."
        keywords="viande bio Le Lude, agriculture biologique 72800, bœuf bio, porc bio, agneau bio, volaille bio, exploitation familiale, producteur local Le Lude, viande qualité supérieure"
        canonical="https://lafermeancienne.fr/"
      />
      <main className="home-page">
        {/* Section Hero */}
        <section className="hero-section" aria-label="Présentation de la ferme">
        <div className="hero-content">
          <div className="hero-text">
            <h1>La Ferme À L'Ancienne</h1>
            <p>
              La ferme à l'ancienne à démarrer cette belle aventure conjointement depuis 2009 en vous proposant divers produits de qualités agréé bio. En effet, plus qu'un travail ce sont nos valeurs aux quotidien. Le respect du bien être animal, des pratiques respectueuses de l'environnement et le maintien de la biodiversité sont des valeurs que nous tenons à vous mettre en avant.
            </p>
            <div className="hero-ab-logo">
              <img src="/images/AB.png" alt="Certification Agriculture Biologique - Logo AB" />
            </div>
            <div className="hero-buttons">
              <button 
                className="cta-primary"
                onClick={() => onProductClick && onProductClick('all')}
              >
                Découvrir nos produits
                <ArrowRight size={18} />
              </button>
              <button 
                className="cta-secondary"
                onClick={() => onProductClick && onProductClick('histoire')}
              >
                Notre histoire
              </button>
            </div>
          </div>
          <div className="hero-image">
            <img src="/images/VACHES.png" alt="Élevage bovin bio à La Ferme À L'Ancienne, Le Lude" />
          </div>
        </div>
      </section>


      {/* Section Produits Vedettes */}
      <section className="featured-products-section">
        <div className="container">
          <div className="section-header">
            <h2>Nos Produits Vedettes</h2>
            <p>Découvrez une sélection de nos meilleurs produits</p>
            <button 
              className="view-all-btn"
              onClick={() => onProductClick && onProductClick('all')}
            >
              Voir tous les produits
              <ArrowRight size={16} />
            </button>
          </div>

          <div className="products-grid">
            {featuredProducts.map(product => (
              <div 
                key={product.id} 
                className="product-card"
                onClick={() => handleProductClick(product)}
              >
                <div className="product-image">
                  <img src={product.image} alt={`${product.name} - ${product.category} bio de qualité supérieure`} />
                  {product.featured && (
                    <div className="featured-badge">
                      <Star size={16} />
                      <span>Vedette</span>
                    </div>
                  )}
                </div>
                
                <div className="product-info">
                  <div className="product-category">{product.category}</div>
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-description">{product.description}</p>
                  
                  <div className="product-footer">
                    <div className="product-price">
                      <span className="price">{product.price}€</span>
                      <span className="unit">/{product.unit}</span>
                    </div>
                    
                    <button 
                      className="add-to-cart-btn"
                      onClick={(e) => handleAddToCart(product, e)}
                    >
                      Ajouter au panier
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      </main>
    </>
  );
};

export default HomePage;
